import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../../firebase";

interface Friend {
  id?: string;
  name: string;
  imageUrl: string;
  relationship: string;
}

const FriendsManagement: React.FC = () => {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [newFriend, setNewFriend] = useState<Friend>({
    name: "",
    imageUrl: "",
    relationship: "",
  });
  const [editingFriend, setEditingFriend] = useState<Friend | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [editImageFile, setEditImageFile] = useState<File | null>(null);

  // Fetch friends from Firestore
  const fetchFriends = async () => {
    setIsLoading(true);
    try {
      const friendsCollection = collection(db, "friends");
      const friendsSnapshot = await getDocs(friendsCollection);
      const friendsList = friendsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Friend[];
      setFriends(friendsList);
    } catch (error) {
      console.error("Error fetching friends:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFriends();
  }, []);

  // Upload image to Firebase Storage
  const uploadImage = async (file: File): Promise<string> => {
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `friends/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };

  // Handle image file selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>, isEdit: boolean = false) => {
    if (e.target.files && e.target.files[0]) {
      if (isEdit) {
        setEditImageFile(e.target.files[0]);
      } else {
        setImageFile(e.target.files[0]);
      }
    }
  };

  // Add a new friend
  const handleAddFriend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFriend.name || (!newFriend.imageUrl && !imageFile) || !newFriend.relationship) {
      alert("Please fill in all fields and provide either an image URL or upload an image");
      return;
    }

    try {
      let finalImageUrl = newFriend.imageUrl;

      // If an image file was selected, upload it and get the URL
      if (imageFile) {
        setUploadingImage(true);
        finalImageUrl = await uploadImage(imageFile);
        setUploadingImage(false);
      }

      const friendsCollection = collection(db, "friends");
      await addDoc(friendsCollection, {
        name: newFriend.name,
        imageUrl: finalImageUrl,
        relationship: newFriend.relationship,
      });
      
      setNewFriend({
        name: "",
        imageUrl: "",
        relationship: "",
      });
      setImageFile(null);
      fetchFriends();
    } catch (error) {
      console.error("Error adding friend:", error);
      setUploadingImage(false);
    }
  };

  // Delete a friend
  const handleDeleteFriend = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this friend?")) {
      try {
        const friendDoc = doc(db, "friends", id);
        await deleteDoc(friendDoc);
        fetchFriends();
      } catch (error) {
        console.error("Error deleting friend:", error);
      }
    }
  };

  // Start editing a friend
  const handleEditStart = (friend: Friend) => {
    setEditingFriend(friend);
    setEditImageFile(null);
  };

  // Cancel editing
  const handleEditCancel = () => {
    setEditingFriend(null);
    setEditImageFile(null);
  };

  // Save edited friend
  const handleEditSave = async () => {
    if (!editingFriend || !editingFriend.id) return;

    try {
      let finalImageUrl = editingFriend.imageUrl;

      // If an image file was selected for editing, upload it and get the URL
      if (editImageFile) {
        setUploadingImage(true);
        finalImageUrl = await uploadImage(editImageFile);
        setUploadingImage(false);
      }

      const friendDoc = doc(db, "friends", editingFriend.id);
      await updateDoc(friendDoc, {
        name: editingFriend.name,
        imageUrl: finalImageUrl,
        relationship: editingFriend.relationship,
      });
      setEditingFriend(null);
      setEditImageFile(null);
      fetchFriends();
    } catch (error) {
      console.error("Error updating friend:", error);
      setUploadingImage(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold mb-4">Add New Friend</h2>
        <form onSubmit={handleAddFriend} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Name
            </label>
            <input
              type="text"
              value={newFriend.name}
              onChange={(e) =>
                setNewFriend({ ...newFriend, name: e.target.value })
              }
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          </div>
          
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Image
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Option 1: Enter Image URL
                </label>
                <input
                  type="text"
                  value={newFriend.imageUrl}
                  onChange={(e) =>
                    setNewFriend({ ...newFriend, imageUrl: e.target.value })
                  }
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Option 2: Upload Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
                {imageFile && (
                  <p className="mt-1 text-xs text-green-600">
                    Selected: {imageFile.name}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Relationship
            </label>
            <input
              type="text"
              value={newFriend.relationship}
              onChange={(e) =>
                setNewFriend({ ...newFriend, relationship: e.target.value })
              }
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              placeholder="e.g., Partner, Corporate, Community"
            />
          </div>
          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={uploadingImage}
              className="bg-gold text-white px-4 py-2 rounded-md hover:bg-gold-dark disabled:bg-gold-light disabled:cursor-not-allowed"
            >
              {uploadingImage ? "Uploading..." : "Add Friend"}
            </button>
          </div>
        </form>
      </div>

      <div>
        <h2 className="text-2xl font-semibold mb-4">Friends List</h2>
        {isLoading ? (
          <p>Loading...</p>
        ) : friends.length === 0 ? (
          <p>No friends found.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {friends.map((friend) => (
              <div
                key={friend.id}
                className="border rounded-md shadow-sm p-4 bg-white"
              >
                {editingFriend && editingFriend.id === friend.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Name
                      </label>
                      <input
                        type="text"
                        value={editingFriend.name}
                        onChange={(e) =>
                          setEditingFriend({
                            ...editingFriend,
                            name: e.target.value,
                          })
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Image
                      </label>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Current Image
                          </label>
                          <div className="h-24 w-24 mb-2">
                            <img
                              src={editingFriend.imageUrl}
                              alt={editingFriend.name}
                              className="h-full w-full object-cover rounded-md"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = "/placeholder-image.svg";
                              }}
                            />
                          </div>
                          <input
                            type="text"
                            value={editingFriend.imageUrl}
                            onChange={(e) =>
                              setEditingFriend({
                                ...editingFriend,
                                imageUrl: e.target.value,
                              })
                            }
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Upload New Image
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageChange(e, true)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                          />
                          {editImageFile && (
                            <p className="mt-1 text-xs text-green-600">
                              Selected: {editImageFile.name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Relationship
                      </label>
                      <input
                        type="text"
                        value={editingFriend.relationship}
                        onChange={(e) =>
                          setEditingFriend({
                            ...editingFriend,
                            relationship: e.target.value,
                          })
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      />
                    </div>
                    <div className="flex space-x-2 mt-2">
                      <button
                        onClick={handleEditSave}
                        disabled={uploadingImage}
                        className="bg-green-600 text-white px-3 py-1 rounded-md text-sm disabled:bg-green-300 disabled:cursor-not-allowed"
                      >
                        {uploadingImage ? "Uploading..." : "Save"}
                      </button>
                      <button
                        onClick={handleEditCancel}
                        className="bg-gray-500 text-white px-3 py-1 rounded-md text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {friend.imageUrl && (
                      <div className="h-40 mb-3">
                        <img
                          src={friend.imageUrl}
                          alt={friend.name}
                          className="h-full w-full object-cover rounded-md"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "/placeholder-image.svg";
                          }}
                        />
                      </div>
                    )}
                    <h3 className="text-lg font-medium">{friend.name}</h3>
                    <p className="text-gray-600">{friend.relationship}</p>
                    <div className="flex space-x-2 mt-2">
                      <button
                        onClick={() => handleEditStart(friend)}
                        className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => friend.id && handleDeleteFriend(friend.id)}
                        className="bg-red-600 text-white px-3 py-1 rounded-md text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendsManagement; 